<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useClipboard, useIntervalFn } from '@vueuse/core';
import axios from 'axios';

import copyIcon from '@/assets/icons/copy.svg';
import scanIcon from '@/assets/icons/scan.svg';
import logoRoundedBackIcon from '@/assets/images/logo-rounded-black.svg';
import bankTransferLottieData from '@/assets/lottie/bank-transfer.json';
import { useBankTransfer } from '@/composables/useBankTransfer';
import { useErrorModal } from '@/composables/useErrorModal';
import { useOtc } from '@/composables/useOtc';
import { useTimeUnix } from '@/composables/useTimeUnix';
import { useTransactions } from '@/composables/useTransactions';
import { capitalize, formatTokenAmount } from '@/helpers/formatters';
import { formatAsHHMMSS } from '@/helpers/formatters/datetime';
import { generateQrCode } from '@/helpers/qrCode';
import { addSentryBreadcrumb, captureSentryException } from '@/logs/sentry';
import { APIError } from '@/references/axios/APIError';
import { AmountMode, type Quote } from '@/references/axios/transactions/types';
import { SECOND } from '@/references/constants';
import { useI18n } from '@/references/i18n';
import { getNetwork } from '@/references/network';
import { RouteNames } from '@/router';

import ConfirmationModal from '@/components/ConfirmationModal.vue';
import { InnerLayoutButtonType } from '@/components/layouts/InnerLayout.types';
import InnerLayout from '@/components/layouts/InnerLayout.vue';
import ProgressLayout from '@/components/layouts/ProgressLayout.vue';
import UiIcon from '@/components/ui/UiIcon.vue';
import type {
  UiListPairGroup,
  UiListPairItem as UiListPairItemInfo
} from '@/components/ui/UiListPair.types';
import UiListPair from '@/components/ui/UiListPair.vue';
import UiLottie from '@/components/ui/UiLottie.vue';
import UiModal from '@/components/ui/UiModal.vue';
import UiProgressCircle from '@/components/ui/UiProgressCircle.vue';
import UiSkeleton from '@/components/ui/UiSkeleton.vue';
import UiText from '@/components/ui/UiText.vue';
import UiTitle from '@/components/ui/UiTitle.vue';

const QR_CODE_SIZE = 224;
const QR_CODE_IMAGE_SIZE = 32;

interface Props {
  id: string;
}

const props = defineProps<Props>();

const state = reactive<{
  isOpenRequoteModal: boolean;
  isLoadingPause: boolean;
  isRefreshingQuote: boolean;
  isModalOpened: boolean;
  isModalButtonLoading: boolean;
  isLoaded: boolean;
}>({
  isOpenRequoteModal: false,
  isLoadingPause: false,
  isRefreshingQuote: false,
  isModalOpened: false,
  isModalButtonLoading: false,
  isLoaded: false
});

const toast = useErrorModal();
const { replace } = useRouter();
const { t } = useI18n();
const { loadFreshQuote } = useOtc();
const { transactions, load: loadTransactions } = useTransactions();
const { tsNow } = useTimeUnix();
const { copy, isSupported } = useClipboard({ legacy: true });
const { isLoaded: isRecipientsLoaded, loadRecipients } = useBankTransfer();

let refreshingQuoteAbortController = new AbortController();
let timerLoader: ReturnType<typeof setTimeout> | undefined = undefined;

const quote = computed((): Quote | undefined => {
  const tx = transactions.value.find((tx) => tx.id === props.id);
  if (tx === undefined) {
    return undefined;
  }

  return tx.quote;
});

const remainingTimeToDeposit = computed((): number => {
  if (quote.value === undefined) {
    return 0;
  }

  return Math.max(quote.value.expiredTs - tsNow.value, 0);
});

const formattedTokenAmount = computed((): string => {
  if (quote.value === undefined) {
    return '';
  }

  return formatTokenAmount(
    quote.value.tokenAmount,
    quote.value.token.decimals,
    quote.value.token.symbol,
    quote.value.token.iconURL === '' ? AmountMode.CodeLast : AmountMode.ValueOnly,
    false,
    2
  );
});

const details = computed((): UiListPairGroup[] => {
  if (quote.value === undefined) {
    return [];
  }

  const networkInfo = getNetwork(quote.value.token.network);
  const networkDisplayedName =
    networkInfo?.displayedName ?? networkInfo?.name ?? capitalize(quote.value.token.network);

  return [
    {
      items: [
        {
          id: 'address',
          label: t('quote.reviewTitles.walletAddress'),
          value: quote.value.receiverWalletAddress,
          copy: quote.value.receiverWalletAddress,
          icon: copyIcon,
          clickable: true
        },
        {
          id: 'generateQr',
          label: t('quote.reviewTitles.generateQr'),
          value: t('quote.reviewTitles.qrCode'),
          icon: scanIcon,
          clickable: true
        },
        {
          label: t('quote.reviewTitles.amount'),
          value: formattedTokenAmount.value,
          image: quote.value.token.iconURL
        },
        {
          label: t('quote.reviewTitles.network'),
          value: networkDisplayedName,
          image: networkInfo?.iconURL
        },
        {
          label: t('quote.reviewTitles.expiresIn'),
          value: formatAsHHMMSS(remainingTimeToDeposit.value)
        }
      ]
    }
  ];
});

const qrCodeDataURL = computed((): string => {
  if (quote.value === undefined) {
    return '';
  }
  return `data:image/svg+xml;base64,${window.btoa(
    generateQrCode(
      quote.value.receiverWalletAddress,
      QR_CODE_SIZE,
      QR_CODE_IMAGE_SIZE,
      '#fafafc',
      '#000'
    )
  )}`;
});

const quoteLifetime = computed((): number => {
  return Math.max((quote.value?.expiredTs ?? 0) - (quote.value?.createdTs ?? 0), 1);
});

const quoteSecondsLeft = computed((): number => {
  return Math.max((quote.value?.expiredTs ?? 0) - tsNow.value, 0);
});

const progress = computed((): number => {
  return (quoteSecondsLeft.value / quoteLifetime.value) * 100;
});

function clickItem(value: UiListPairItemInfo): void {
  switch (value.id) {
    case 'address': {
      if (!isSupported.value) {
        toast.notify(t('errors.clipboardCopyNotSupported'));
        return;
      }

      copy(value.value ?? '');
      break;
    }
    case 'generateQr': {
      openQrModal();
      break;
    }
  }
}

function closeQrModal(): void {
  state.isModalOpened = false;
}

function closeRequoteModal(): void {
  replace({ name: RouteNames.Home });
}

async function confirmRequote(): Promise<void> {
  if (quote.value === undefined) {
    replace({ name: RouteNames.Home });
    return;
  }

  if (!isRecipientsLoaded.value) {
    try {
      state.isModalButtonLoading = true;
      await loadRecipients();
    } catch {
      /* */
    } finally {
      state.isModalButtonLoading = false;
    }
  }

  state.isOpenRequoteModal = false;
  replace({ name: RouteNames.OTC, params: { id: quote.value.beneficiary.id } });
}

function openQrModal(): void {
  state.isModalOpened = true;
}

function goBack(): void {
  replace({ name: RouteNames.Activity });
}

function startLoaderTimer(): void {
  state.isLoadingPause = true;
  timerLoader = setTimeout(() => {
    replace({ name: RouteNames.Activity, params: { id: props.id } });
  }, 5 * SECOND);
}

const timer = useIntervalFn(async () => {
  if (state.isRefreshingQuote) {
    return;
  }
  const q = quote.value;
  if (q === undefined) {
    return;
  }
  state.isRefreshingQuote = true;
  refreshingQuoteAbortController = new AbortController();

  try {
    const freshQuote = await loadFreshQuote(q.quoteId, refreshingQuoteAbortController.signal);
    if (freshQuote.status === q.status) {
      const ts = Math.max(freshQuote.expiredTs - tsNow.value, 0);
      if (ts === 0) {
        state.isOpenRequoteModal = true;
        timer.pause();
        return;
      }
      return;
    }
    addSentryBreadcrumb({
      level: 'debug',
      message: 'detect new quote status, reloading txs'
    });
    await loadTransactions();
    if (freshQuote.status !== 'awaits-crypto') {
      startLoaderTimer();
      timer.pause();
      return;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    addSentryBreadcrumb({
      level: 'error',
      message: 'fail to reload quote',
      data: {
        error: e
      }
    });
    captureSentryException(new Error('Failed to reload quote', { cause: e }));

    if (e instanceof APIError && e.message === 'NOT_FOUND') {
      replace({ name: RouteNames.Activity, params: { id: props.id } });
      return;
    }
  } finally {
    state.isLoaded = true;
    state.isRefreshingQuote = false;
  }
}, SECOND);

onBeforeUnmount(() => {
  refreshingQuoteAbortController.abort();
  clearTimeout(timerLoader);
  timer.pause();
});

watch(
  [quote, () => state.isLoaded],
  () => {
    if (!state.isLoaded) {
      return;
    }

    if (quote.value === undefined) {
      replace({ name: RouteNames.Activity, params: { id: props.id } });
    }
  },
  { immediate: true }
);
</script>

<template>
  <section class="watch-quote-view">
    <InnerLayout
      v-if="!state.isLoadingPause"
      title=" "
      :button-type="InnerLayoutButtonType.Close"
      @back="goBack"
    >
      <div class="watch-quote-view__inner">
        <template v-if="quote">
          <div class="watch-quote-view__icon-wrapper">
            <UiLottie
              class="watch-quote-view__icon"
              :data="bankTransferLottieData"
              :size="36"
              autoplay
              loop
            />
            <UiProgressCircle
              class="watch-quote-view__progress"
              :progress="progress"
              :size="52"
              :stroke="12"
            />
          </div>
          <UiTitle class="watch-quote-view__title" :level="1">
            {{ t('quote.transferToken', { token: quote?.token.symbol ?? '' }) }}
          </UiTitle>
          <UiText class="watch-quote-view__description" tag="p" muted>
            {{ t('quote.reviewTitles.description') }}
          </UiText>
          <UiListPair class="watch-quote-view__list" :details="details" @click:item="clickItem" />
        </template>
        <template v-else>
          <UiSkeleton
            class="watch-quote-view__skeleton watch-quote-view__skeleton_icon"
            border-radius="50%"
            width="48px"
            height="48px"
          />
          <UiSkeleton
            class="watch-quote-view__skeleton watch-quote-view__skeleton_title"
            width="180px"
            height="24px"
          />
          <UiSkeleton
            class="watch-quote-view__skeleton watch-quote-view__skeleton_description"
            width="240px"
            height="16px"
          />
        </template>
      </div>
      <UiModal
        :model-value="state.isModalOpened && !state.isOpenRequoteModal"
        @update:model-value="closeQrModal"
      >
        <div class="watch-quote-view__modal-qr-wrapper">
          <img class="watch-quote-view__modal-qr-image" :src="qrCodeDataURL" alt="" />
          <UiIcon
            class="watch-quote-view__modal-qr-logo"
            :name="logoRoundedBackIcon"
            alt=""
            :size="32"
          />
        </div>
        <UiText
          v-if="quote?.receiverWalletAddress"
          class="watch-quote-view__modal-qr-description"
          size="xs"
          tag="p"
          muted
          align="center"
        >
          {{ quote?.receiverWalletAddress ?? '' }}
        </UiText>
      </UiModal>
      <ConfirmationModal
        :title="t('quote.requoteModal.title')"
        :cancel-button-text="t('quote.requoteModal.cancel')"
        :confirm-button-text="t('quote.requoteModal.confirm')"
        :model-value="state.isOpenRequoteModal"
        :loading="state.isModalButtonLoading"
        @update:model-value="closeRequoteModal"
        @confirm="confirmRequote"
      >
        {{ t('quote.requoteModal.description') }}
      </ConfirmationModal>
    </InnerLayout>
    <ProgressLayout v-else :lottie="bankTransferLottieData" :title="t('otc.otc')" />
  </section>
</template>

<style scoped>
.watch-quote-view {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.watch-quote-view__inner {
  padding: 24px 0 0;
}

.watch-quote-view__icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin: 0 0 24px;
}

.watch-quote-view__progress {
  position: absolute;
  top: 0;
  left: 0;
}

.watch-quote-view__title {
  margin: 0 0 8px;
}

.watch-quote-view__description {
  margin: 0 0 24px;
}

.watch-quote-view__skeleton_icon {
  margin: 2px 0 34px;
}

.watch-quote-view__skeleton_title {
  margin: 0 0 20px;
}

.watch-quote-view__skeleton_description {
  margin: 0 0 32px;
  max-width: 100% !important;
}

.watch-quote-view__skeleton-list {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.watch-quote-view__skeleton-item {
  display: flex;
  justify-content: space-between;
}

.watch-quote-view__skeleton-item:not(:last-child) {
  margin: 0 0 22px;
}

.watch-quote-view__modal-qr-wrapper {
  position: relative;
  max-width: 274px;
  margin: 16px auto 24px;
  padding: 24px;
  background: var(--color-qr-background);
  border: 1px solid var(--color-main-stroke);
  border-radius: 30px;
}

.watch-quote-view__modal-qr-image {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
}

.watch-quote-view__modal-qr-logo {
  position: absolute;
  top: calc(50% - 16px);
  left: calc(50% - 16px);
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 50%;
}

.watch-quote-view__modal-qr-description {
  word-break: break-word;
  margin: 0 auto;
}
</style>
